import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaArrowRight
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './FeatureSlider.css';

const SLIDES = [
  {
    image: '/promo-images/home-loans.svg',
    badge: 'Loan Promo',
    title: 'Smart Loan Planning Starts Here',
    description: 'Use EMI, prepayment and eligibility calculators to compare scenarios before you apply.',
    ctaText: 'Explore Loan Calculators',
    ctaPath: '/emi',
  },
  {
    image: '/promo-images/tax-fy.svg',
    badge: 'Tax Season',
    title: 'FY 2025-26 Tax Ready Tools',
    description: 'Calculate tax under old/new regime, estimate TDS and optimize deductions in minutes.',
    ctaText: 'Open Tax Calculator',
    ctaPath: '/income-tax',
  },
  {
    image: '/promo-images/invest-growth.svg',
    badge: 'Investment Focus',
    title: 'Build Wealth With SIP, FD, PPF & More',
    description: 'Track future value and growth of your investments with simple visual calculators.',
    ctaText: 'Try SIP Calculator',
    ctaPath: '/sip',
  },
];

const AUTOPLAY_MS = 5000;

const FeatureSlider = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  const goTo = useCallback((i) => {
    setActive(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);
  }, []);
  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setActive((a) => (a + 1) % SLIDES.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  return (
    <div
      className="feature-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="region"
      aria-roledescription="carousel"
      aria-label="paisapyara features"
    >
      <div
        className="feature-slider-track"
        style={{ transform: `translateX(-${active * 100}%)` }}
      >
        {SLIDES.map((slide, i) => {
          return (
            <div
              className="feature-slide"
              key={i}
              aria-hidden={i !== active}
            >
              <img
                className="feature-slide-image"
                src={slide.image}
                alt={slide.title}
                loading="lazy"
                width="1200"
                height="675"
              />
              <div className="feature-slide-overlay" />
              <div className="feature-slide-inner">
                <span className="feature-slide-badge">{slide.badge}</span>
                <h2 className="feature-slide-title">{slide.title}</h2>
                <p className="feature-slide-desc">{slide.description}</p>
                <Link to={slide.ctaPath} className="feature-slide-cta">
                  {slide.ctaText} <FaArrowRight />
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="feature-slider-arrow feature-slider-arrow-prev"
        onClick={prev}
        aria-label="Previous feature"
      >
        ‹
      </button>
      <button
        type="button"
        className="feature-slider-arrow feature-slider-arrow-next"
        onClick={next}
        aria-label="Next feature"
      >
        ›
      </button>

      <div className="feature-slider-dots" role="tablist">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`feature-slider-dot${i === active ? ' is-active' : ''}`}
            onClick={() => goTo(i)}
            role="tab"
            aria-selected={i === active}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default FeatureSlider;

